import { useEffect, useState } from "react"
import { Formik, Form } from "formik"
import axios from "axios"
import { alpha } from "@mui/material/styles"
import { API_URL } from "../../../../constants"
import CardMarkerSmall from "../../../../components/CockpitCards/CardMarkerSmall"
import SubSubText from "./SubSubText.jsx"
import ChangeSubText from "./ChangeSubText.jsx"
import ButtonsMain from "../../../../components/Button/ButtonMain"
import ButtonForm from "../../../../components/Button/ButtonForm"
import PaymentMethodCard from "./PaymentMethodCard.jsx"
import InvoicesCard from "./InvoicesCard.jsx"
import {
  Typography,
  Stack,
  Chip,
  Divider,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slide,
  IconButton,
  useTheme,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import UpgradeIcon from "@mui/icons-material/Upgrade"
import CancelIcon from "@mui/icons-material/Cancel"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import { useTranslation } from "../../../../language"

const SlideUp = (props) => <Slide direction="up" {...props} />

const PAYMENT_INTERVAL_KEYS = {
  yearly: "sub_per_year",
  quarterly: "sub_per_quarter",
  monthly: "sub_per_month",
}

const paymentIntervalKey = (interval) => PAYMENT_INTERVAL_KEYS[interval] || "sub_per_year"

const PLAN_NAME_KEYS = {
  plan2: "sub_plan2_name",
  plan3: "sub_plan3_name",
  plan4: "sub_plan4_name",
  plan5: "sub_plan5_name",
}

const planName = (plan, t) => {
  const key = PLAN_NAME_KEYS[plan.translationkey]
  return key ? t(key) : plan.name
}

// Liquid glass shell, same recipe used elsewhere in the app (translucent
// fill + blur + inset highlight).
function glassCardSx(theme) {
  const isDark = theme.palette.mode === "dark"
  return {
    bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
    boxShadow: isDark
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  }
}

export default function AccountSubscription() {

  const theme = useTheme()
  const { t } = useTranslation()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/account/subscription/`)
      setData(res.data)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleCancel = async () => {
    setActionLoading(true)
    setActionError(null)
    try {
      await axios.post(`${API_URL}/api/account/subscription/cancel/`)
      await fetchData()
    } catch (err) {
      setActionError(err.response?.data?.message || t("sub_action_error"))
    } finally {
      setActionLoading(false)
    }
  }

  const handleResume = async () => {
    setActionLoading(true)
    setActionError(null)
    try {
      await axios.post(`${API_URL}/api/account/subscription/resume/`)
      await fetchData()
    } catch (err) {
      setActionError(err.response?.data?.message || t("sub_action_error"))
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) return <Skeleton height={240} />
  if (error) return <Typography color="error">{t("sub_fetch_error")}</Typography>
  if (!data) return null

  const { subscription, current_plan, available_plans } = data

  if (!current_plan) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 800, mx: "auto", width: "100%", px: { xs: 2, sm: 0 }, pb: 6 }}>
        <CardMarkerSmall
          sx={glassCardSx(theme, theme.palette.tertiary.C300)}
          headline={t("sub_headline")}
          subtext={<SubSubText />}
          cardlines={<Typography color="text.secondary">{t("sub_no_plan")}</Typography>}
        />
        <PaymentMethodCard sx={glassCardSx(theme, theme.palette.tertiary.C300)} />
        <InvoicesCard sx={glassCardSx(theme, theme.palette.quartiary.C300)} />
      </Stack>
    )
  }

  return (
    <>
      <Stack spacing={3} sx={{ maxWidth: 800, mx: "auto", width: "100%", px: { xs: 2, sm: 0 }, pb: 6 }}>

        <CardMarkerSmall
          sx={glassCardSx(theme, theme.palette.tertiary.C300)}
          headline={t("sub_headline")}
          subtext={<SubSubText />}
          cardlines={
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={4} justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{planName(current_plan, t)}</Typography>
                <Chip
                  icon={subscription.is_active ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={subscription.is_active ? t("sub_status_active") : t("sub_status_inactive")}
                  color={subscription.is_active ? "success" : "default"}
                />
              </Stack>

              <Divider />

              <Stack spacing={1} sx={{ mt: 1, px: 4, py: 2, bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C960", borderRadius: 4 }}>
                <InfoRow label={t("sub_billing_period")} stacked>
                  {subscription.current_period_start} {"->"} {subscription.current_period_end}
                </InfoRow>
                <InfoRow label={t("sub_price")}>
                  {"€"} {Number(current_plan.price_eur).toFixed(2)} {t(paymentIntervalKey(current_plan.payment_interval))}
                </InfoRow>
              </Stack>

              {subscription.cancel_at_period_end && (
                <Chip color="warning" label={t("sub_cancels_at_period_end")} />
              )}
            </Stack>
          }
        />

        <CardMarkerSmall
          sx={glassCardSx(theme, theme.palette.quartiary.C300)}
          headline={t("sub_manage_headline")}
          subtext={<ChangeSubText />}
          cardlines={
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <ButtonsMain
                  startIcon={<UpgradeIcon />}
                  name={t("sub_change_plan")}
                  action={() => setUpgradeOpen(true)}
                />
                {!subscription.cancel_at_period_end ? (
                  <ButtonsMain
                    startIcon={<CancelIcon />}
                    color="error"
                    disabled={actionLoading}
                    action={handleCancel}
                    name={t("sub_cancel")}
                  />
                ) : (
                  <ButtonsMain
                    color="success"
                    disabled={actionLoading}
                    action={handleResume}
                    name={t("sub_resume")}
                  />
                )}
              </Stack>
              {actionError && (
                <Alert severity="error" onClose={() => setActionError(null)}>
                  {actionError}
                </Alert>
              )}
            </Stack>
          }
        />

        <PaymentMethodCard sx={glassCardSx(theme, theme.palette.tertiary.C300)} />

        <InvoicesCard sx={glassCardSx(theme, theme.palette.quartiary.C300)} />

      </Stack>

      <Dialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        TransitionComponent={SlideUp}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiDialog-paper": { bgcolor: theme.palette.background.dialog, borderRadius: "12px", py: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.background.dialog }}>
          {t("sub_change_plan_dialog")}
          <IconButton
            aria-label="close"
            onClick={() => setUpgradeOpen(false)}
            sx={{ position: "absolute", right: 16, top: 16, color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={{ product_id: current_plan.id }}
          onSubmit={async (values, { setStatus }) => {
            try {
              await axios.post(`${API_URL}/api/account/subscription/change-plan/`, values)
              setUpgradeOpen(false)
              await fetchData()
            } catch (err) {
              setStatus(err.response?.data?.message || t("sub_plan_change_error"))
            }
          }}
        >
          {({ values, setFieldValue, isSubmitting, status }) => (
            <Form>
              <DialogContent sx={{
                bgcolor: theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.background.dialog,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}>
                <RadioGroup
                  value={values.product_id}
                  onChange={(e) => setFieldValue("product_id", e.target.value)}
                >
                  {available_plans.map((plan) => (
                    <FormControlLabel
                      key={plan.id}
                      value={plan.id}
                      control={<Radio />}
                      sx={{ mb: 2 }}
                      label={
                        <Stack>
                          <Typography fontWeight={600}>{plan.name}</Typography>
                          <Typography variant="subtitle1" fontWeight={400}>
                            {"€"} {Number(plan.price_eur).toFixed(2)} {t(paymentIntervalKey(plan.payment_interval))}
                          </Typography>
                        </Stack>
                      }
                    />
                  ))}
                </RadioGroup>

                <Alert severity="info">{t("sub_proration_notice")}</Alert>

                {status && (
                  <Alert severity="error">{status}</Alert>
                )}

                <Stack direction="row" spacing={4} justifyContent="flex-end">
                  <ButtonForm
                    disabled={isSubmitting}
                    name={t("sub_confirm_change")}
                  />
                </Stack>
              </DialogContent>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}


function InfoRow({ label, children, stacked }) {
  if (stacked) {
    return (
      <Stack spacing={0.25}>
        <Typography variant="subtitle1" fontWeight={400}>{label}</Typography>
        <Typography variant="subtitle1" fontWeight={400}>{children}</Typography>
      </Stack>
    )
  }
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="subtitle1" fontWeight={400}>{label}</Typography>
      <Typography variant="subtitle1" fontWeight={400}>{children}</Typography>
    </Stack>
  )
}
